import { InventoryTransactionType, IssueTaskDoc, IssueTaskStatus } from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { FC, useState } from 'react';

import { formatDate } from 'src/utils/date-util';
import { callInventoryIssueTransaction } from 'src/utils/firebase/firebase-callable';
import { updateIssueTask } from 'src/utils/firebase/firebase-short-cut';
import { validateInventoryIssueInfo } from 'src/utils/issue-task-util';

import Description from 'src/atomic-components/atoms/Description';
import IssueCardSubTitle from 'src/atomic-components/atoms/IssueCardItem/IssueCardSubTitle';
import { IssueInfoItem } from 'src/atomic-components/atoms/IssueInfoItem/IssueInfoItem';
import IssueTaskInventoryProductItem from 'src/atomic-components/atoms/IssueTaskInventoryProductItem/IssueTaskInventoryProductItem';

interface IssueTaskInventoryIssueInfoProps {
  issueTaskDoc: IssueTaskDoc;
}

const IssueTaskInventoryIssueInfo: FC<IssueTaskInventoryIssueInfoProps> = ({ issueTaskDoc }) => {
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const validation = validateInventoryIssueInfo(issueTaskDoc);

  // 검증 실패 - 에러 메시지가 있으면 표시, 없으면 null (표시 안함)
  if (!validation.isValid) {
    return (
      <div>
        <IssueCardSubTitle text='회수 상품 재고 처리' />
        <IssueInfoItem label='⚠️ error' value={validation.errorMessage ?? '알 수 없는 에러'} />
      </div>
    );
  }

  // 검증 통과 - 정상 렌더링 (검증된 데이터 사용)
  const { products, inventoryIssueInfo } = validation.data;
  const isCompleted = issueTaskDoc.status === IssueTaskStatus.COMPLETED;

  const handleStartInbound = async (productId: string, reason: string, quantity: number) => {
    if (!reason || reason.trim() === '') {
      message.error('입고 사유를 입력해주세요.');
      return;
    }

    if (quantity <= 0) {
      message.error('입고 수량은 0보다 커야 합니다.');
      return;
    }

    setLoadingProductId(productId);

    try {
      const product = products.find((p) => p.productId === productId);
      if (!product) {
        throw new Error('상품을 찾을 수 없습니다.');
      }

      if (quantity > product.quantity) {
        throw new Error(`입고 수량은 최대 ${product.quantity}까지 가능합니다.`);
      }

      // 오늘 날짜
      const issueDate = formatDate(new Date(), 'yyyy-MM-dd');

      // 재고 이슈 생성 (입고)
      await callInventoryIssueTransaction({
        body: {
          issueDate,
          inventoryId: product.inventoryId,
          inventoryUnit: product.inventoryUnit,
          transactionType: InventoryTransactionType.INBOUND,
          requestValue: quantity,
          reason,
          byIssueTask: {
            issueTaskId: issueTaskDoc._id,
            productId,
          },
        },
      });
    } catch (error) {
      console.error('재고 입고 처리 실패:', error);
    }
    setLoadingProductId(null);
  };

  const handleSkip = async (productId: string) => {
    setLoadingProductId(productId);
    try {
      await updateIssueTask(issueTaskDoc._id, {
        [`inventoryIssueInfo.products.${productId}`]: {
          status: 'skipped',
          inventoryIssueId: null,
          processedAt: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
        },
      });
      message.success('입고 처리를 건너뜁니다.');
    } catch (error) {
      console.error('입고 처리 건너뛰기 실패:', error);
      message.error('입고 처리 건너뛰기에 실패했습니다.');
    }
    setLoadingProductId(null);
  };

  return (
    <div>
      <IssueCardSubTitle
        text='회수 상품 입고 처리'
        tooltip='입고 처리를 진행하면 연결된 Case와 Task 모두 취소할 수 없습니다.'
      />
      {!isCompleted && (
        <Description style={{ padding: '0px 8px 8px 8px' }}>* 업무 완료 후 처리 가능합니다.</Description>
      )}
      {products.map((product) => {
        const productInfo = inventoryIssueInfo.products[product.productId];

        return (
          <IssueTaskInventoryProductItem
            key={product.productId}
            product={product}
            productStatus={{
              status: productInfo?.status || 'pending',
              inventoryIssueId: productInfo?.inventoryIssueId,
              processedAt: productInfo?.processedAt,
            }}
            storeName={issueTaskDoc.storeName}
            editable={isCompleted && loadingProductId !== product.productId}
            onStartInbound={(reason, quantity) => handleStartInbound(product.productId, reason, quantity)}
            onSkip={() => handleSkip(product.productId)}
          />
        );
      })}
    </div>
  );
};

export default IssueTaskInventoryIssueInfo;
